import type { RetryOptions } from './types';

const TRANSIENT_ERROR_CODES = ['ETIMEDOUT', 'ECONNRESET', 'ECONNREFUSED', 'ENOTFOUND', 'EAI_AGAIN'];

export function isTransientNetworkError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const code = (error as { code?: string }).code;
  if (code && TRANSIENT_ERROR_CODES.includes(code)) {
    return true;
  }

  const cause = (error as { cause?: { code?: string } }).cause;
  if (cause?.code && TRANSIENT_ERROR_CODES.includes(cause.code)) {
    return true;
  }

  return false;
}

export async function withRetry<T>(fn: () => Promise<T>, options?: RetryOptions): Promise<T> {
  const raw = options?.maxRetries ?? 1;
  const maxRetries = Number.isFinite(raw) ? Math.max(0, raw) : 1;
  const baseDelayMs = options?.baseDelayMs ?? 200;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      const isLastAttempt = attempt === maxRetries;
      if (isLastAttempt || !isTransientNetworkError(error)) {
        throw error;
      }

      const delay = baseDelayMs * 2 ** attempt;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  // Unreachable — the loop always returns or throws
  throw new Error('withRetry: unreachable');
}

export function isServer(): boolean {
  return typeof window === 'undefined';
}
